import React, { useEffect, useMemo, useState } from 'react'
import classes from './LanguageSelector.module.scss'
import Locale from './Locale'
import useContentStack from '../hooks/useContentStack'
import { ContentStackTypes } from '../store/Constants'
import Button from '@veneer/core/dist/esm/scripts/button'
import IconX from '@veneer/core/dist/esm/scripts/icons/icon_x'
import useAnalytics from '../hooks/useAnalytics'
import { ANALYTICS } from '../store/AnalyticsConstants'
import CurrentLocale from './CurrentLocale'
import { useAnalyticsProps } from '../hooks/useAnalyticsProps'
import useMediaQuery from '@veneer/core/dist/esm/scripts/utils/use_media_query'

const LanguageSelector = ({ localization, toggleButtonAnalytics }) => {
  const [showLanguageSelector, setShowLanguageSelector] = useState(false)
  const [languages, setLanguages] = useState(null)
  const [sortedLocales, setSortedLocales] = useState(null)
  const [currentCountryName, setCurrentCountryName] = useState('')
  const {
    getLanguages,
    setLocale,
    country: currentCountry,
    language: currentLanguage
  } = localization
  const chineseCountriesGroup = ['ZH_CN', 'ZH_HK', 'ZH_TW']
  const hideFlag = ['ZH_HK', 'ZH_TW']
  const currentLanguageCode = `${currentLanguage}_${currentCountry}`
  const isTablet = useMediaQuery('(max-width: 1007px)')
  const isSmallDesktop = useMediaQuery('(max-width: 1499px)')
  const columnCount = isTablet ? 2 : isSmallDesktop ? 3 : 5

  const { fireButtonClick, fireScreenDisplayed } = useAnalytics(
    ANALYTICS.MODALS.LANGUAGE_SELECTOR,
    true
  )

  const analyticsProps = useAnalyticsProps()

  const { pageData } = useContentStack(ContentStackTypes.language_selector)

  useEffect(() => {
    const getShellLanguages = async () => {
      const data = await getLanguages()
      setLanguages(data)
    }
    getShellLanguages()
  }, [showLanguageSelector])

  const toggleLanguageSelector = () => {
    toggleButtonAnalytics(ANALYTICS.BUTTONS.LANGUAGE_SELECTOR, analyticsProps)
    if (!showLanguageSelector) {
      fireScreenDisplayed(analyticsProps)
    }
    setShowLanguageSelector(!showLanguageSelector)
  }

  const onCloseLanguageSelector = () => {
    fireButtonClick(ANALYTICS.BUTTONS.CLOSE_LANGUAGE_SELECTOR, analyticsProps)
    setShowLanguageSelector(false)
  }

  const currentPageDataLocale = useMemo(
    () =>
      pageData?.locales?.find(
        (loc) =>
          loc.language.toLowerCase() === currentLanguageCode ||
          (loc?.fallback_languages?.toLowerCase()?.split(';') || []).includes(
            currentLanguageCode.toLowerCase()
          )
      ),
    [pageData?.locales]
  )

  const localeColumns = useMemo(() => {
    if (!sortedLocales?.length) {
      return []
    }

    const rowsPerColumn = Math.ceil(sortedLocales.length / columnCount)

    return Array.from({ length: columnCount }, (_, columnIndex) => {
      const startIndex = columnIndex * rowsPerColumn
      const endIndex = startIndex + rowsPerColumn
      return sortedLocales.slice(startIndex, endIndex)
    }).filter((column) => column.length > 0)
  }, [sortedLocales, columnCount])

  useEffect(() => {
    if (!languages || !pageData) {
      return
    }
    const languageNames = new Map(
      pageData?.locales?.map((locale) => [
        locale?.language?.toUpperCase(),
        locale
      ])
    )
    const locales = []
    let zhGroup = []
    Object.keys(languages).forEach((languageCode) =>
      languages[languageCode].forEach((countryCode) => {
        const langCode = `${languageCode}_${countryCode}`.toUpperCase()
        const languageName = languageNames.get(langCode)
        if (!languageName?.name) {
          return
        }
        if (!chineseCountriesGroup.includes(langCode)) {
          locales.push({
            countryCode: countryCode,
            languageCode: languageCode,
            ...languageName
          })
        } else {
          zhGroup.push({
            countryCode: countryCode,
            languageCode: languageCode,
            ...languageName
          })
        }
      })
    )
    locales.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
    setSortedLocales([...locales, ...zhGroup])
    setCurrentCountryName(
      languageNames.get(`${currentLanguage}_${currentCountry}`.toUpperCase())
        ?.name
    )
  }, [languages, pageData, currentCountry, currentLanguage])

  return (
    <div className={classes.selectorLink}>
      {showLanguageSelector && (
        <>
          <div
            data-testid="language-selector-background"
            className={classes.background}
          />
          <div
            className={classes.languageSelectorContainer}
            id="language-selector-container"
            onClick={(event) => {
              if (event.target.id === 'language-selector-container') {
                onCloseLanguageSelector()
              }
            }}
          >
            <div
              className={classes.languageSelector}
              data-testid={'language-selector-panel'}
            >
              <div className={classes.languageSelectorContent}>
                <div className={classes.header}>
                  <h4>{pageData?.header}</h4>
                  <div>
                    <div>
                      <CurrentLocale
                        country={currentPageDataLocale?.language.split('_')[1]}
                        name={currentPageDataLocale?.name}
                        showFlag={
                          !hideFlag.includes(currentLanguageCode?.toUpperCase())
                        }
                      />
                    </div>
                    <Button
                      data-testid="language-selector-close-button"
                      leadingIcon={<IconX />}
                      appearance="ghost"
                      onClick={() => {
                        onCloseLanguageSelector()
                      }}
                    />
                  </div>
                </div>
                <div className={classes.content}>
                  {localeColumns.map((column, columnIndex) => (
                    <div
                      className={classes.contentColumn}
                      key={`locale-column-${columnIndex}`}
                    >
                      {column.map((locale) => {
                        return (
                          <Locale
                            showFlag={
                              !hideFlag.includes(locale.language?.toUpperCase())
                            }
                            key={`${locale.languageCode}_${locale.countryCode}`}
                            country={locale.countryCode}
                            language={locale.languageCode}
                            name={locale.name}
                            dir={locale.text_direction?.toLowerCase()}
                            onclick={() => {
                              fireButtonClick(
                                ANALYTICS.BUTTONS.SELECT_LANGUAGE,
                                {
                                  ...analyticsProps,
                                  detail: `${locale.countryCode?.toLowerCase?.()}-${locale.languageCode?.toLowerCase?.()}`
                                }
                              )
                              setLocale({
                                country: locale.countryCode.toLowerCase(),
                                language: locale.languageCode.toLowerCase()
                              })
                            }}
                          />
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div data-testid={'language-selector-link'}>
        {currentPageDataLocale ? (
          <Locale
            showFlag={!hideFlag.includes(currentLanguageCode?.toUpperCase())}
            country={currentPageDataLocale.language.split('_')[1]}
            language={currentPageDataLocale.language.split('_')[0]}
            name={currentPageDataLocale.name}
            onclick={toggleLanguageSelector}
          />
        ) : (
          <Locale
            showFlag={!hideFlag.includes(currentLanguageCode?.toUpperCase())}
            country={currentCountry}
            language={currentLanguage}
            name={currentCountryName}
            onclick={toggleLanguageSelector}
          />
        )}
      </div>
    </div>
  )
}

export default LanguageSelector
